import { Trans } from '@lingui/react/macro';
import { type FC, type PropsWithChildren, type SVGProps } from 'react';

import { type HealthCenterPodType } from '@/api/healthCenter';
import { LinkButton } from '@/components/buttons';
import Icon from '@/components/icon';
import InfoPopover from '@/components/InfoPopover';
import { SkeletonRectangle } from '@/components/loaders';
import { Text, Title } from '@/components/typography';
import { cn } from '@/lib/utils';
import { useMe } from '@/providers/User';

import HealthPodStatusTag from './HealthPodStatusTag';

type HealthPodProps = {
  isLoading: boolean;
  isNext?: boolean;
  priority?: number;
  icon: FC<SVGProps<SVGSVGElement>>;
  title: string;
  titleInfoPopover?: {
    title: React.ReactNode;
    body: React.ReactNode;
  };
  instructions: string;
  link?: string;
  data?: HealthCenterPodType;
};

const HealthPod = ({
  isLoading,
  isNext = false,
  priority,
  icon,
  title,
  titleInfoPopover,
  instructions,
  link,
  data,
  children,
}: PropsWithChildren<HealthPodProps>) => {
  const user = useMe();
  const isPaidUser = user?.isPaid;
  const { isAvailable, isComplete, isFree } = data ?? {};
  const notIncludedInPlan = !isPaidUser && !isFree;

  const disabled = notIncludedInPlan || !isAvailable || isLoading;

  return (
    <article
      className={cn(
        'flex h-full flex-col gap-3 rounded-2xl border-2 border-gray-300 bg-white p-5',
        isNext && 'border-green-400',
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        {priority != null && (
          <Text
            variant="body1"
            weight="bold"
            aria-hidden="true"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100',
              isNext && 'bg-primary',
            )}
          >
            {priority}
          </Text>
        )}
        <Icon size="large" icon={icon} />
        <Title level={3} weight="bold" className="min-w-40 flex-1">
          <span className="flex items-center gap-1">
            {title}
            {titleInfoPopover && (
              <InfoPopover title={titleInfoPopover.title} body={titleInfoPopover.body} />
            )}
          </span>
        </Title>
        {!isLoading && (
          <HealthPodStatusTag
            isAvailable={isAvailable}
            notIncludedInPlan={notIncludedInPlan}
            isComplete={isComplete}
            disabled={disabled}
            link={link}
          />
        )}
        {!isLoading && link && (
          <LinkButton to={link} variant={isNext ? 'primary' : 'secondary'} disabled={disabled}>
            {isNext ? <Trans>Start setup</Trans> : <Trans>Take Me There</Trans>}
          </LinkButton>
        )}
      </div>
      <Text variant="footnote">{instructions}</Text>
      {isLoading ? (
        <SkeletonRectangle width="50%" height={16} />
      ) : (
        <Text variant="body2" color="dark-grey" className="flex flex-wrap gap-1">
          {children}
        </Text>
      )}
    </article>
  );
};

export default HealthPod;
